/*
  ATHLO+ — Member profile + achievements engine  →  window.HubProfile
  ===========================================================================
  The personalisation/identity layer that sits on top of HubMember (identity),
  HubSave (favourites) and HubSocial ("I'm in"/who's-in). It owns the rich
  athlete profile: bio, interests, PBs, upcoming races, links, years-on-platform,
  an earned-titles ACHIEVEMENTS engine and a derived Hub Level / status.

  STORAGE
    - Owner's own profile lives in localStorage 'hub:profile' (extends the existing
      {name,avatar} shape used by hub-membership.js / hub-social.js / hub-locker —
      additive, never drops those keys), so editing works instantly, per-device,
      with NO backend.
    - When the Community backend is configured (Theme settings → Community →
      endpoint + mode, same one HubSocial uses), the profile SYNCS to it and OTHER
      members' profiles become viewable. INERT until then — zero network calls.

  BACKEND CONTRACT (additive to the HubSocial contract)
    POST {action:'profile', member_id, profile:<json>}   -> { ok }
    GET  ?profile=<member_id>[&callback=fn]               -> { id, ...profileFields }
    (the older ?member= GET still returns name/avatar/archetype/interests)

  ACHIEVEMENTS / TITLES
    A catalogue of earnable titles, each with a test() over a derived context
    (member?, saves, joins, archetype, profile fields, years-on-platform).
    Defaults live here; a merchant can OVERRIDE/extend them with hub_achievement
    metaobjects (window.HUB_ACHIEVEMENTS_MO) — editable as metadata, inert until
    migrated. Earned achievements => titles shown on the profile; points => Hub Level.

  Everything render-safe: this module only stores/derives data. Sections render it
  with textContent / createElement (localStorage + backend are untrusted).
*/
(function () {
  'use strict';
  var KEY = 'hub:profile';
  var B = document.body;

  // Architect / developer unlock. Normally set by layout/theme.liquid when the
  // signed-in customer's email matches. New passwordless accounts often DON'T
  // expose customer.email to Liquid, so honour a local dev override too:
  // run  localStorage.setItem('hub:architect','1')  in the console once.
  try { if (localStorage.getItem('hub:architect') === '1') window.HUB_ARCHITECT = true; } catch (e) {}

  // ---- Community backend (shared with HubSocial) --------------------------
  var EP = (B && B.getAttribute('data-social-endpoint') || '').trim();
  var MODE = (B && B.getAttribute('data-social-mode') || 'appsscript').trim();
  function backendOn() { return !!EP; }
  // per-customer write token. Sent with every write so the backend can verify the writer is
  // genuinely this member. `social_mode` is `rest`, so these writes go DIRECT to the community
  // function rather than through the signed App Proxy — which means community/index.ts only accepts
  // them on the token branch. This token is load-bearing here, not belt-and-braces.
  //
  // 2026-08-29: ASK HubSession FIRST. This read used to go straight to the body attribute, which was
  // fine only while Liquid always rendered a permanent token there. Once `locker_token_in_dom` was
  // switched off (correctly — it was permanent and non-revocable) the attribute is empty until
  // hub-session.js mints a short-lived one and writes it back, so a direct read is a race against a
  // network round trip. Asking HubSession removes the race in the common case: it returns the minted
  // token the moment it exists, and falls back to the same attribute otherwise. Same bug family as
  // the Strava connect outage of 2026-08-28 — see assets/hub-strava.js.
  function lockerToken() {
    try {
      var s = window.HubSession;
      var t = (s && typeof s.token === 'function' && s.token()) || '';
      if (t) return String(t).trim();
      return (B && B.getAttribute('data-locker-token') || '').trim();
    } catch (e) { return ''; }
  }

  // ---- the canonical interest / PB-distance vocab (editable below) --------
  var INTERESTS = [
    'Running', 'Trail', 'Cycling', 'Triathlon', 'Swimming', 'Hyrox', 'CrossFit',
    'Strength', 'Yoga', 'Climbing', 'Hiking', 'Tennis', 'Badminton', 'Football',
    'Basketball', 'Boxing', 'Pickleball', 'Padel', 'Dance', 'Pilates', 'Golf', 'Rowing'
  ];
  var PB_DISTANCES = ['5K', '10K', '21.1K', '42.2K', 'Swim 1.5K', 'Bike 40K', 'Hyrox'];

  // ---- profile model ------------------------------------------------------
  function thisYear() { return new Date().getFullYear(); }
  function archetype() {
    try { var a = JSON.parse(localStorage.getItem('hub:archetype') || 'null'); if (a && a.title) return a; } catch (e) {}
    return null;
  }
  function blank() {
    return {
      name: (window.HubMember && window.HubMember.name) || '',
      avatar: '🏃',
      avatarKit: null,          // composed "Create Your Athlete" SVG kit (window.HubAvatar)
      avatarKits: [],           // saved looks — up to 3 slots, [{ kit }] | null per slot
      gender: '',
      bio: '',
      homeBase: '',
      since: thisYear(),
      dob: '',               // 'YYYY-MM-DD' — drives U18 stealth mode (never shown publicly)
      interests: [],
      pbs: {},               // { '5K': '21:30', ... } — flat, drives achievements
      pbsX: [],              // Build-a-Stat: [{ sport, event, unit, value, updated }]
      races: [],             // [{ name, date, url }]
      links: {},             // { ig, strava, web }
      tier: '',              // merchant membership tier (Smile) — optional, editable
      bgHex: '',             // profile background hex (validated) — '' = theme default
      photo: '',             // flattened WebP data URL (HTML5 canvas, transparency removed)
      titleAdj: '', titleNoun: '', // two-slot custom flex (unlocked separately)
      slug: '',              // clean public URL handle (aldrich → /a/profile/aldrich)
      privacy: {},           // { age, metrics, clubs, socials } true = hidden on public profile
      unlocks: {},           // { achievementId: ISO8601 } — timestamped for "Year in Review"
      updated: 0,
      v: 2                   // schema version (for the "Grandfather Protocol")
    };
  }
  function get() {
    var p = blank();
    try { var raw = JSON.parse(localStorage.getItem(KEY) || '{}') || {}; for (var k in raw) if (raw.hasOwnProperty(k)) p[k] = raw[k]; } catch (e) {}
    // RE-SEED A BLANK NAME FROM THE SHOPIFY FIRST NAME (2026-08-19). blank() already seeds it,
    // but the merge above is key-by-key and a STORED '' outranks the seed forever. The quiz
    // calls HubProfile.persist() the instant it has a result, and the designed funnel is quiz
    // first / join after — so that first write lands while HubMember.name is still '' and bakes
    // name:'' into localStorage permanently. That is the whole reason 10 of the 24 members with
    // a profile listed as "ATHLETE" in the directory while Shopify held their first name; six of
    // them heal on their next visit because of these two lines. A blank name is not a choice the
    // locker offers (the field is optional, not clearable-to-anonymous), so treat it as absent.
    // The server keeps the richer value once set — mergeProfile skips empty incoming fields — so
    // this can only ever fill a gap, never overwrite a name the member typed.
    if (!String(p.name || '').trim()) p.name = (window.HubMember && window.HubMember.name) || '';
    if (!p.interests || !p.interests.slice) p.interests = [];
    if (!p.pbs || typeof p.pbs !== 'object') p.pbs = {};
    if (!p.pbsX || !p.pbsX.slice) p.pbsX = [];
    if (!p.races || !p.races.slice) p.races = [];
    // Saved looks are capped here as well as in HubAvatar.normLoadouts(): this store is what
    // syncs, so a blob that arrived with 40 slots must not be handed to the server intact.
    if (!p.avatarKits || !p.avatarKits.slice) p.avatarKits = [];
    else if (p.avatarKits.length > 3) p.avatarKits = p.avatarKits.slice(0, 3);
    if (!p.links || typeof p.links !== 'object') p.links = {};
    if (!p.privacy || typeof p.privacy !== 'object') p.privacy = {};
    if (!p.unlocks || typeof p.unlocks !== 'object') p.unlocks = {};
    if (!p.since) p.since = thisYear();
    return p;
  }
  function set(p) {
    p.updated = Date.now();
    // stamp each Build-a-Stat PB with a UTC edit time (Year-in-Review substrate)
    if (p.pbsX && p.pbsX.forEach) { var now = new Date().toISOString(); p.pbsX.forEach(function (r) { if (r && !r.updated) r.updated = now; }); }
    try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
    emit(p);
    sync(p);
    return p;
  }
  function patch(obj) {
    var p = get();
    for (var k in obj) if (obj.hasOwnProperty(k)) p[k] = obj[k];
    return set(p);
  }
  // Re-stamp + sync the current profile without changing any field — used when a
  // DERIVED input changed elsewhere (e.g. the quiz just wrote a new archetype into
  // hub:archetype). sync() reads archetype() into the blob, so this pushes the new
  // archetype to the server and bumps `updated` so it wins cross-device.
  function persist() {
    var p = get();
    p.updated = Date.now();
    try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
    emit(p);
    sync(p);
    return p;
  }

  // ---- event bus ----------------------------------------------------------
  function emit(p) { try { window.dispatchEvent(new CustomEvent('hub:profile', { detail: p || get() })); } catch (e) {} }
  function on(fn) { window.addEventListener('hub:profile', function (e) { fn(e.detail); }); }

  // ---- analytics ----------------------------------------------------------
  function track(name, detail) {
    if (typeof window.gtag === 'function') window.gtag('event', name, detail || {});
    if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: name }, detail || {}));
  }

  // ---- derived signals ----------------------------------------------------
  function savesCount() { try { return (window.HubSave && window.HubSave.count && window.HubSave.count()) || 0; } catch (e) { return 0; } }
  // Lifetime completed orders — injected by the locker section as window.HUB_ORDERS
  // (= Shopify customer.orders_count). Drives the 🛒 First Purchase achievement.
  function ordersCount() { var n = parseInt(window.HUB_ORDERS, 10); return isFinite(n) && n > 0 ? n : 0; }
  // Verified referral CONVERSIONS this member drove — set by hub-loyalty.js from the loyalty
  // backend's ?state (a friend who was referred placed their first paid order). Drives the
  // 🤝 Spread the Word achievement as a real unlock, not just copying the invite link.
  function referralsCount() { var n = parseInt(window.HUB_REFERRALS, 10); return isFinite(n) && n > 0 ? n : 0; }
  function yearsOn(p) { p = p || get(); var y = thisYear() - (p.since || thisYear()); return y < 0 ? 0 : y; }
  function joinsCount(cb) {
    // events the member has said "I'm in" to — needs the backend; 0 otherwise.
    var m = window.HubMember;
    if (!backendOn() || !m || !m.is() || !m.id) { cb(0); return; }
    jget(EP + (EP.indexOf('?') < 0 ? '?' : '&') + 'member=' + encodeURIComponent(m.id), function (d) {
      cb((d && d.interests && d.interests.length) || 0);
    });
  }

  // ---- achievements catalogue (overridable via HUB_ACHIEVEMENTS_MO) -------
  // ctx = { member, saves, joins, years, archetype, p }.  Quiz-flavoured, lots of
  // long-tail grind goals so the wall never clears fast. RULES live here; the COPY
  // (icon/title/desc/pts) is overridable by hub_achievement metaobjects (by id).
  // Map Build-a-Stat event names → the legacy flat-PB distance keys the
  // achievements engine tests, so "one place to log stats" still lights up
  // the 5K / marathon / tri badges. (Personal Bests are now just starred stats.)
  var DISTANCE_ALIAS = {
    '5k': '5K', '10k': '10K',
    'half marathon': '21.1K', 'half': '21.1K', '21.1k': '21.1K', '21k': '21.1K',
    'marathon': '42.2K', 'full marathon': '42.2K', '42.2k': '42.2K', '42k': '42.2K',
    '1.5k swim': 'Swim 1.5K', 'swim 1.5k': 'Swim 1.5K',
    '40k tt': 'Bike 40K', 'bike 40k': 'Bike 40K', '40k': 'Bike 40K'
  };
  // The single source of truth for "do they have a PB at distance d": legacy
  // p.pbs (grandfathered) merged with anything logged in Build-a-Stat (pbsX).
  function flatPbs(p) {
    p = p || get();
    var flat = {}, legacy = p.pbs || {};
    for (var k in legacy) if (legacy[k] && String(legacy[k]).trim()) flat[k] = legacy[k];
    (p.pbsX || []).forEach(function (r) {
      if (!r || !r.value || r.hidden) return;   // soft-deleted / dismissed PBs never count
      var key = DISTANCE_ALIAS[String(r.event || '').toLowerCase().trim()];
      if (key && !flat[key]) flat[key] = r.value;
    });
    return flat;
  }
  // Up to 3 Build-a-Stat entries the member starred as Personal Bests (★ pb:true).
  // Falls back to their first 3 stats so a PB section still shows if none starred.
  function featuredPbs(p) {
    p = p || get();
    var x = (p.pbsX || []).filter(function (r) { return r && r.value && !r.hidden; });   // dismissed PBs never show
    var starred = x.filter(function (r) { return r.pb; });
    return (starred.length ? starred : x).slice(0, 3);
  }
  function pb(c, d) { var f = flatPbs(c.p); return !!(f[d] && String(f[d]).trim()); }
  function tier(c) { return (c.archetype && (c.archetype.tier || '')) + ''; }
  // rarePct() lived here and read archetype.pct — the fabricated rarity percentage
  // removed on 2026-08-05. Nothing called it, and left in place it would have
  // returned its 999 "unknown" sentinel forever. Compare on tier/standing instead.
  var CATALOGUE = [
  // Only the ✅-ticked achievements from the Membership sheet award points & exist as
  // badges. ❌-crossed rows were removed entirely. 23 achievements, 575 pts total
  // (= Pro tier @ 500). See DENOMINATIONS below.
    { id: 'roster',   icon: '🪪', title: 'Fresh Meat',         desc: 'Signed up for ATHLO+',                       pts: 20, cat: 'Onboarding', test: function (c) { return c.member; } },
    { id: 'globe',    icon: '📍', title: 'Pin Dropped',        desc: 'Set your home base',                          pts: 10, cat: 'Profile', test: function (c) { return !!(c.p.homeBase || '').trim(); } },
    { id: 'repping',  icon: '🧍', title: 'Repping',            desc: 'Told us how you show up',                     pts: 10, cat: 'Profile',  test: function (c) { return !!(c.p.gender || '').trim(); } },
    { id: 'know',     icon: '🧬', title: 'Knows Thyself',      desc: 'Found your athlete archetype',                pts: 20, cat: 'Profile', test: function (c) { return !!c.archetype; } },
    { id: 'story',    icon: '✍️', title: 'Storyteller',        desc: 'Wrote a bio',                                 pts: 10, cat: 'Profile', test: function (c) { return (c.p.bio || '').trim().length >= 20; } },
    { id: 'plugged',  icon: '🔗', title: 'Plugged In',         desc: 'Linked Instagram + Strava',                   pts: 30, cat: 'Profile', test: function (c) { return !!((c.p.links || {}).ig) && !!((c.p.links || {}).strava); } },
    { id: 'rounded',  icon: '🎯', title: 'All-Rounder',        desc: 'Flagged 3+ interests',                        pts: 10, cat: 'Profile', test: function (c) { return c.p.interests.length >= 3; } },
    { id: 'pb1',      icon: '⏱️', title: 'On the Board',       desc: 'Logged a personal best',                      pts: 20, cat: 'Sports & Performance', test: function (c) { return pbCount(c.p) >= 1; } },
    { id: 'tri',      icon: '🏊', title: 'Tri-Curious',        desc: 'Logged swim, bike AND run bests',             pts: 30, cat: 'Sports & Performance', test: function (c) { return pb(c, 'Swim 1.5K') && pb(c, 'Bike 40K') && (pb(c, '5K') || pb(c, '10K') || pb(c, '21.1K') || pb(c, '42.2K')); } },
    { id: 'race',     icon: '🏁', title: 'Race Ready',         desc: 'Logged a race result',                        pts: 20, cat: 'Events', test: function (c) { return c.p.races.length >= 1; } },
    { id: 'save1',    icon: '❤️', title: 'Curator',            desc: 'Saved your first thing',                      pts: 5, cat: 'Engagement',  test: function (c) { return c.saves >= 1; } },
    { id: 'join1',    icon: '🤝', title: 'Joiner',             desc: 'Said "I\'m in" to an event',                  pts: 20, cat: 'Events', test: function (c) { return c.joins >= 1; } },
    { id: 'join3',    icon: '🦋', title: 'Social Butterfly',   desc: 'Joined 3 events',                             pts: 30, cat: 'Events', test: function (c) { return c.joins >= 3; } },
    { id: 'join10',   icon: '📣', title: 'Ringleader',         desc: 'Joined 10 events',                            pts: 50, cat: 'Events', test: function (c) { return c.joins >= 10; } },
    { id: 'sportkit', icon: '👕', title: 'Repping the Sport',   desc: 'Equipped a sport kit on your avatar',          pts: 5, cat: 'Profile', test: function (c) { return !!(c.p.avatarKit && c.p.avatarKit.sport && c.p.avatarKit.sport !== 'none'); } },
    { id: 'shot',     icon: '📸', title: 'Say Cheese',          desc: 'Uploaded a profile photo',                    pts: 5, cat: 'Profile', test: function (c) { return !!(c.p.photo || '').length; } },
    { id: 'builder',  icon: '🧱', title: 'Stat Builder',        desc: 'Logged a Build-a-Stat PB',                    pts: 20, cat: 'Sports & Performance', test: function (c) { return (c.p.pbsX || []).length >= 1; } },
    { id: 'fin1',     icon: '🎽', title: 'Toed the Line',       desc: 'Finished your first race',                    pts: 30, cat: 'Sports & Performance', test: function (c) { return medals(c.p).finisher >= 1; } },
    { id: 'founder',  icon: '🏗️', title: 'Founding Athlete',    desc: 'On ATHLO+ since 2024 or earlier',            pts: 50, cat: 'Legacy', test: function (c) { return c.p.since && c.p.since <= 2024; } },
    { id: 'kitted',   icon: '✅', title: 'Fully Kitted',       desc: 'Completed the core profile',                  pts: 30, cat: 'Onboarding', test: function (c) { return !!c.p.avatar && (c.p.bio || '').trim().length >= 20 && c.p.interests.length >= 1 && pbCount(c.p) >= 1; } },
    { id: 'profile100', icon: '🎁', title: 'Starter Pack',      desc: '100% profile complete — starter gear unlocked', pts: 50, cat: 'Onboarding', test: function (c) { return completion(c.p).pct === 100; } },
    { id: 'firstbuy', icon: '🛒', title: 'First Purchase',     desc: 'Made your first purchase',                    pts: 50, cat: 'Engagement', test: function (c) { return (c.orders || 0) >= 1; } },
    { id: 'refer',    icon: '🤝', title: 'Spread the Word',     desc: 'A friend you referred made their first order', pts: 50, cat: 'Engagement', test: function (c) { return (c.referrals || 0) >= 1; } },
    // Lights Out (2026-10-09): awarded by the grid backend to the top 10 of the Singapore race-weekend grid once it
    // locks (profile.frontRow = { pos, ms, year }). No points — a title for the cabinet, not a payout. Server-set.
    { id: 'frontrow', icon: '🚦', title: 'Front Row',           desc: 'Top 10 on the ATHLO+ grid at lights out, Singapore race weekend 2026. Wah, so fast.', pts: 0, cat: 'Legacy', test: function (c) { return !!(c.p.frontRow && c.p.frontRow.pos); } },
    // P1 of the same grid: the same server stamp, pos 1. Both badges, one winner a year.
    { id: 'pole26',   icon: '🏆', title: 'Pole Position',       desc: 'Fastest off the line on the ATHLO+ grid, Singapore race weekend 2026. The whole island behind you.', pts: 0, cat: 'Legacy', test: function (c) { return !!(c.p.frontRow && c.p.frontRow.pos === 1); } },
    // Virtual run (2026-10-17): stamped by the virtual-run function from APPROVED runs into profile.vrun.badges
    // (server-owned in the community function, so a save can neither forge nor drop one). No points — a sponsor
    // can fund them later; ids match BADGES in supabase-community/.../virtual-run/lib/core.ts. `secret`: hidden until earned.
    { id: 'vrun_first',    icon: '👟', title: 'Uncancelled',      desc: 'Logged a run in an ATHLO+ virtual run.',              pts: 0, cat: 'Events', secret: true, test: function (c) { return vrunBadge(c, 'first'); } },
    { id: 'vrun_km50',     icon: '🛣️', title: '50 km club',       desc: '50 km run in ATHLO+ virtual runs.',                    pts: 0, cat: 'Events', secret: true, test: function (c) { return vrunBadge(c, 'km50'); } },
    { id: 'vrun_haze',     icon: '🌫️', title: 'Haze-proof',       desc: 'Took it indoors on a haze day.',                      pts: 0, cat: 'Events', secret: true, test: function (c) { return vrunBadge(c, 'haze'); } },
    { id: 'vrun_streak7',  icon: '📅', title: 'Seven straight',   desc: 'Ran seven days in a row.',                            pts: 0, cat: 'Events', secret: true, test: function (c) { return vrunBadge(c, 'streak7'); } },
    { id: 'vrun_finisher', icon: '🏅', title: 'Virtual finisher', desc: 'Finished a race distance in an ATHLO+ virtual race.', pts: 0, cat: 'Events', secret: true, test: function (c) { return vrunBadge(c, 'finisher'); } }
  ];
  function vrunBadge(c, id) { var v = c.p.vrun; return !!(v && v.badges && v.badges[id]); }
  function catalogue() {
    var mo = window.HUB_ACHIEVEMENTS_MO;
    if (!mo || !mo.length) return CATALOGUE;
    // metaobjects override copy (icon/title/desc/pts) by id; tests stay in code.
    var byId = {}; CATALOGUE.forEach(function (a) { byId[a.id] = a; });
    mo.forEach(function (m) {
      var id = m.id || m.handle; if (!id) return;
      var base = byId[id] || { id: id, test: function () { return false; } };
      byId[id] = {
        id: id, test: base.test,
        icon: m.icon || base.icon || '🏅',
        title: m.title || base.title || id,
        desc: m.desc || base.desc || '',
        pts: m.pts != null ? +m.pts : (base.pts || 10),
        cat: m.cat || base.cat || ''
      };
    });
    // preserve catalogue order, then append any new MO-only entries.
    var out = CATALOGUE.map(function (a) { return byId[a.id]; });
    mo.forEach(function (m) { var id = m.id || m.handle; if (id && !CATALOGUE.some(function (a) { return a.id === id; })) out.push(byId[id]); });
    return out;
  }
  function pbCount(p) { var f = flatPbs(p), n = 0; for (var k in f) if (f[k] && String(f[k]).trim()) n++; return n; }
  // Derive a race's medal from a NUMERIC finishing position (athletes type a
  // number, not pick a dropdown). 1/2/3 → gold/silver/bronze, 4-8 track → top8,
  // 4-10 → top10, 11+ → finisher. A "world major / big race" earns a ⭐ on top of the medal.
  // Legacy races that stored a `podium` string still resolve.
  var MEDAL_ICON = { gold: '🥇', silver: '🥈', bronze: '🥉', top8: '🔷', top10: '🏅', finish: '🎖️', dnf: '❌' };
  var MEDAL_LABEL = { gold: 'Gold', silver: 'Silver', bronze: 'Bronze', top8: 'Top 8', top10: 'Top 10', finish: 'Finisher', dnf: 'DNF' };
  function medalFor(r) {
    r = r || {};
    var tier = '';
    if (r.dnf) tier = 'dnf';
    else {
      var pos = parseInt(r.position, 10);
      if (isFinite(pos) && pos >= 1) {
        if (pos === 1) tier = 'gold'; else if (pos === 2) tier = 'silver'; else if (pos === 3) tier = 'bronze';
        else if (pos <= 8 && r.track) tier = 'top8'; // track race, finals have 8 people
        else if (pos <= 10) tier = 'top10'; else tier = 'finish';
      } else if (r.podium) { tier = r.podium; }        // grandfathered dropdown value
    }
    return {
      tier: tier, star: !!r.major, dnf: tier === 'dnf',
      icon: MEDAL_ICON[tier] || '', label: MEDAL_LABEL[tier] || ''
    };
  }
  // A race's rank for "top 5 of all time" (lower = better; DNF/blank sink to bottom).
  function raceRank(r) {
    var md = medalFor(r), order = { gold: 1, silver: 2, bronze: 3, top8: 4, top10: 5, finish: 6, dnf: 8 };
    var base = order[md.tier] || 7;
    return base - (md.star ? 0.5 : 0);                  // majors edge ahead within a tier
  }
  // Best 5 race results, ranked (medal first, then majors, then most recent).
  function topRaces(p, n) {
    p = p || get();
    return (p.races || []).slice().sort(function (a, b) {
      var d = raceRank(a) - raceRank(b); if (d) return d;
      return String(b.date || '') < String(a.date || '') ? -1 : 1;
    }).slice(0, n || 5);
  }
  // Podium / medal tally across all logged races.
  //  · placement tiers (gold/silver/bronze/top8/top10/finish) = how you placed in each race
  //  · finisher = EVERY race you completed (non-DNF) — the participation medal, so an
  //    athlete who never podiums still earns hardware just for showing up and finishing.
  //  · placed = races where you actually placed (podium/top8/top10) — for milestone badges.
  function medals(p) {
    p = p || get();
    var m = { gold: 0, silver: 0, bronze: 0, top8: 0, top10: 0, finish: 0, star: 0, dnf: 0, podium: 0, finisher: 0, placed: 0 };
    (p.races || []).forEach(function (r) {
      var md = medalFor(r);
      if (md.dnf) { m.dnf++; return; }        // DNF = didn't finish → no finisher medal
      m.finisher++;                            // completed the race → participation/finisher medal
      if (md.star) m.star++;
      if (md.tier && m.hasOwnProperty(md.tier)) m[md.tier]++;
    });
    m.podium = m.gold + m.silver + m.bronze;
    m.placed = m.podium + m.top8 + m.top10;    // any ranked finish
    return m;
  }
  // ---- multi-sport helpers (so gamification rewards every discipline, not just running) ----
  // A logged Build-a-Stat PB in a given sport (case-insensitive).
  function pbHasSport(p, s) {
    s = String(s || '').toLowerCase();
    return (p.pbsX || []).some(function (r) { return r && r.value && String(r.sport || '').toLowerCase() === s; });
  }
  // How many distinct sports the athlete has logged a PB in.
  function pbSportCount(p) {
    var seen = {}; (p.pbsX || []).forEach(function (r) { if (r && r.value && r.sport) seen[String(r.sport).toLowerCase()] = 1; });
    return Object.keys(seen).length;
  }

  // ---- Hub Level / status from ACHIEVEMENT points -------------------------
  // Distinct from the spend-based membership tiers (Rookie/Pro/Elite in the loyalty
  // program — see the section's tier blocks & the Earn Rate sheet). This ladder is the
  // badge-points "status" score. Per the Denomination & Conversion sheet the point-
  // awarding achievements total 575 — enough to cross the membership Pro threshold (500).
  // Thresholds are calibrated to 0–575 so every tier (incl. the top) is reachable.
  var LEVELS = [
    { name: 'Rookie', min: 0 },
    { name: 'Contender', min: 70 },
    { name: 'Pro', min: 175 },
    { name: 'Elite', min: 320 },
    { name: 'Legend', min: 460 },
    { name: 'G.O.A.T.', min: 575 }
  ];
  // ---- Denomination & Conversion (reference — mirrors the Membership sheet) --------
  // Every point-awarding achievement snaps to one of these 5 denominations. The full set
  // of 23 point-awarding achievements total 575 points (Micro 3×5 + Standard 4×10 +
  // Core 6×20 + Milestone 5×30 + Elite 5×50).
  var DENOMINATIONS = [
    { tier: 'T1 – Micro',     points: 5,  purpose: 'Small profile/setup actions' },
    { tier: 'T2 – Standard',  points: 10, purpose: 'Meaningful onboarding actions' },
    { tier: 'T3 – Core',      points: 20, purpose: 'Key activation milestones' },
    { tier: 'T4 – Milestone', points: 30, purpose: 'Harder engagement milestones' },
    { tier: 'T5 – Elite',     points: 50, purpose: 'Exceptional or rare achievements' }
  ];
  // Redemption economics live in the loyalty program (Smile) — see the Earn Rate sheet.
  // 1 point = $0.02 when redeemed ($10 reward ÷ 500 pts). Effective discount rate =
  // point value × points-per-$ spent, so it climbs with the spend tier:
  //   Rookie  @≥0     — 1×    pt/$1 → 2.0% back
  //   Pro     @≥500   — 1.25× pt/$1 → 2.5% back
  //   Elite   @≥2000  — 1.5×  pt/$1 → 3.0% back
  // (The theme only DISPLAYS these — actual spend-point accrual/redemption is Smile-side.)
  var POINT_VALUE_USD = 0.02;
  var EARN_RATE = { Rookie: { min: 0, perDollar: 1, discount: 0.02 }, Pro: { min: 500, perDollar: 1.25, discount: 0.025 }, Elite: { min: 2000, perDollar: 1.5, discount: 0.03 } };
  function levelFor(pts) { var lv = LEVELS[0]; for (var i = 0; i < LEVELS.length; i++) if (pts >= LEVELS[i].min) lv = LEVELS[i]; return lv; }
  function nextLevel(pts) { for (var i = 0; i < LEVELS.length; i++) if (LEVELS[i].min > pts) return LEVELS[i]; return null; }

  // Evaluate the whole achievement state. Async because joins need the backend.
  function evaluate(cb, pOverride) {
    var p = pOverride || get();
    var m = window.HubMember;
    // referrals: the live verified count is the VIEWER's (window.HUB_REFERRALS), so only trust it
    // for the member's own locker (no override). A public profile carries its own count, if any.
    var base = { member: !!(m && m.is && m.is()), saves: savesCount(), orders: ordersCount(), referrals: (pOverride ? (p.referrals || 0) : referralsCount()), years: yearsOn(p), archetype: archetype(), p: p };
    function finish(joins) {
      base.joins = joins;
      var cat = catalogue(), earned = [], pts = 0;
      cat.forEach(function (a) { var got = false; try { got = !!a.test(base); } catch (e) {} if (got) { earned.push(a); pts += (a.pts || 0); } });
      var lvl = levelFor(pts), nxt = nextLevel(pts);
      var comp = completion(p);
      // 100% completion earns "starter gear" — surfaced as an earned id for the avatar.
      var earnedIds = earned.map(function (a) { return a.id; });
      if (comp.pct === 100 && earnedIds.indexOf('profile100') < 0) earnedIds.push('profile100');
      var state = {
        earned: earned,                 // [{id,icon,title,desc,pts}]
        earnedIds: earnedIds,           // incl. synthetic 'profile100' starter-gear grant
        titles: earned.map(function (a) { return a.title; }),
        // A `secret` badge (the virtual run's) is listed only once earned: no locked teaser before a campaign exists.
        catalogue: cat.filter(function (a) { return !a.secret || earned.indexOf(a) >= 0; }),
        points: pts,
        level: lvl.name,
        next: nxt ? { name: nxt.name, need: nxt.min - pts } : null,
        progress: nxt ? Math.min(1, (pts - lvl.min) / (nxt.min - lvl.min)) : 1,
        completion: comp,               // { items, done, total, pct }
        starterGear: comp.pct === 100 ? STARTER_GEAR.slice() : [],
        context: base
      };
      state.titlesUnlocked = titlesUnlocked(state);   // { adj:[{w,unlocked}], noun:[...] }
      if (!pOverride) { state.unlocks = logUnlocks(earned); } // stamp own unlocks only
      cb(state);
    }
    if (pOverride) { finish((p.interests && p._joins) || 0); return; } // public profiles carry their own count
    joinsCount(finish);
  }

  // ---- backend sync + public fetch ---------------------------------------
  function jget(url, cb) {
    // NOTE: r.ok is checked deliberately. This used to be `return r.json()` with no status check, so a
    // 401 whose BODY happened to look like a success payload (e.g. claimslug returns {slug:...} even
    // when it rejects you) was handed to callers as if it had worked. That is how a broken slug claim
    // reported success for a week. Any non-2xx is now a failure -> cb(null).
    if (MODE === 'rest') {
      fetch(url).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(cb).catch(function () { cb(null); });
      return;
    }
    // appsscript → JSONP (avoids CORS on Apps Script)
    var fn = 'hubp_' + Math.random().toString(36).slice(2);
    window[fn] = function (d) { cb(d); try { delete window[fn]; } catch (e) {} s.remove(); };
    var s = document.createElement('script');
    s.src = url + (url.indexOf('?') < 0 ? '?' : '&') + 'callback=' + fn;
    s.onerror = function () { cb(null); try { delete window[fn]; } catch (e) {} s.remove(); };
    document.head.appendChild(s);
  }
  var PENDING_KEY = 'hub:profile:pending'; // a save awaiting server confirmation (durable across reloads)
  function sync(p) {
    var m = window.HubMember;
    if (!backendOn() || !m || !m.is() || !m.id) return;
    // U18 stealth: never publish DOB, and strip socials from the public blob for minors.
    var minor = u18(p), pubLinks = minor ? {} : p.links;
    var blob = { name: p.name, avatar: p.avatar, avatarKit: p.avatarKit, avatarKits: p.avatarKits, gender: p.gender, bio: p.bio, homeBase: p.homeBase, since: p.since,
      interests: p.interests, pbs: p.pbs, pbsX: p.pbsX, races: p.races, links: pubLinks, tier: p.tier,
      bgHex: p.bgHex, titleAdj: p.titleAdj, titleNoun: p.titleNoun, slug: p.slug, privacy: p.privacy, minor: minor,
      // Explicit proof that minor:false is a REAL result and not just "this device has no dob".
      // The server only accepts a minor true->false transition when this is true, so a lost/absent
      // dob can never silently un-hide an under-18 member. Leaks nothing: it is derived on-device.
      minorCleared: !minor && /^\d{4}-\d{2}-\d{2}$/.test(p.dob || ''),
      // Birthday reward: sync MONTH-DAY only ('MM-DD'), never the year — the birthday bonus can match
      // the date without the backend ever knowing the member's age (under-18 privacy preserved).
      bday: /^\d{4}-\d{2}-\d{2}$/.test(p.dob || '') ? (p.dob || '').slice(5) : '',
      unlocks: p.unlocks, updated: p.updated,
      architect: !!window.HUB_ARCHITECT,
      // Tier + sub-tier ride along so a PUBLIC profile can show the worn title, not
      // just the archetype name. The blob is stored as opaque JSON, so extra keys
      // are additive and safe for older readers.
      archetype: (archetype() || {}).title || '',
      archetypeTier: (archetype() || {}).tier || '',
      archetypeSub: (archetype() || {}).sub || '',
      // THE WHOLE ARCHETYPE, not just the three fields a public profile prints. Added 2026-08-12
      // because sync() was a ONE-WAY door: it wrote the archetype to the server and nothing ever
      // wrote it back, so hydrateOwn() could not restore it and the flat three could not have
      // rebuilt it anyway (no sport, no standing). See restoreArchetype() for the bug that cost.
      // `null` when this device has never taken the quiz — mergeProfile() on the server treats an
      // empty value as "keep the richer stored one", so a fresh browser cannot blank it.
      arch: archetype() };
    postProfile({ action: 'profile', member_id: m.id, profile: JSON.stringify(blob), token: lockerToken() }, 0);
    track('profile_save', { interests: p.interests.length, pbs: pbCount(p) });
  }
  // Fire one POST; resolves true only on a confirmed 2xx (rest). appsscript is opaque
  // (no-cors) so we can only confirm the request left the browser.
  /* A REFUSED TOKEN GETS REPLACED, NOT RE-SENT (2026-08-29).
     The retry below used to re-stamp the body with whatever `lockerToken()` returned, which after an
     expiry is the SAME dead token — so a 401 burned all five attempts on a credential the server had
     already rejected, and the save never landed while the tab stayed open. The session TTL is an hour
     and the backend explicitly expects the client to re-mint on 401; this is where it does it. */
  function sendProfile(body) {
    if (MODE === 'rest') {
      return fetch(EP, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) {
          if (r && (r.status === 401 || r.status === 403)) {
            try { console.error('[hub-profile] save refused (' + r.status + ') — re-minting the locker session and retrying.'); } catch (e) {}
            try { if (window.HubSession && window.HubSession.refresh) window.HubSession.refresh(); } catch (e) {}
          }
          if (!r || !r.ok) throw 0;
          return true;
        });
    }
    var fd = new URLSearchParams(); for (var k in body) fd.append(k, body[k]);
    return fetch(EP, { method: 'POST', mode: 'no-cors', body: fd }).then(function () { return true; });
  }
  // Reliable push: stash the payload as PENDING until the server confirms; retry with
  // backoff on failure, always re-sending the LATEST pending (never a stale superseded
  // one). This is what makes "the newest edit always reaches the server" true even
  // across a dropped request, tab close, or offline moment — flushPending() finishes it.
  /* 2026-08-29: RE-STAMP THE TOKEN ON EVERY ATTEMPT, and never spend an attempt on an empty one.
     Two faults were stacked here, and the second multiplied the first:

       1. The body is built by the caller, so `token` is captured at click time. If the session mint
          had not landed yet, that was an empty string and the write was refused.
       2. The retry re-posted `JSON.parse(raw)` — the SAME body, with the SAME empty token — so one
          mistimed save burned all five attempts and then gave up, silently, leaving the pending blob
          in localStorage. A save that could have succeeded two seconds later never did.

     Now: the token is re-read immediately before each send, and if there is none we WAIT for the mint
     rather than spending an attempt. `HubSession.ready` is already in flight by the time anything
     here runs, so this normally resolves instantly.

     The pending blob is deliberately left in place when we still cannot get a token — that is what
     the profile-rescue path reads on the next page load, and dropping it would turn a recoverable
     save into lost data. */
  function stampToken(body) {
    try { if (body && typeof body === 'object' && 'token' in body) body.token = lockerToken(); } catch (e) {}
    return body;
  }

  function postProfile(body, attempt) {
    var stamp = JSON.stringify(body);
    try { localStorage.setItem(PENDING_KEY, stamp); } catch (e) {}

    /* ASK FOR A *CURRENT* TOKEN, NOT THE ONE BOOT HAPPENED TO GET.
       `HubSession.ready` is settled once, at page load. Awaiting it an hour later returns the token
       that mint resolved with — expired — or the empty string if that first mint failed, and either
       way replays the boot outcome forever. `ensure()` re-mints when the held token is stale or
       missing, so a save is gated on a credential that is good NOW. It resolves instantly in the
       normal case, because a fresh token short-circuits before any network call. */
    var sess = (window.HubSession && window.HubSession.ensure) ? window.HubSession : null;
    var haveToken = sess ? sess.isFresh() : !!lockerToken();
    var gate = haveToken
      ? Promise.resolve()
      : (sess ? sess.ensure()
              : ((window.HubSession && window.HubSession.ready) || Promise.resolve())).catch(function () {});

    gate.then(function () {
      stampToken(body);
      if (!body.token) {
        // No credential and none coming. Sending anyway would be refused and would look, to the
        // member, exactly like a successful save. Keep the pending blob and say so where it can be
        // seen — hub-errlog pages on console errors, so this is not shouting into a void.
        try { console.error('[hub-profile] no locker token — profile write held, not sent. Pending save kept for retry.'); } catch (e) {}
        return;
      }
      return sendProfile(body).then(function () {
        var cur = null; try { cur = localStorage.getItem(PENDING_KEY); } catch (e) {}
        if (cur === stamp) { try { localStorage.removeItem(PENDING_KEY); } catch (e) {} } // confirmed & not superseded
      }).catch(function () {
        if ((attempt || 0) < 4) setTimeout(function () {
          var raw = null; try { raw = localStorage.getItem(PENDING_KEY); } catch (e) {}
          if (raw) { try { postProfile(stampToken(JSON.parse(raw)), (attempt || 0) + 1); } catch (e) {} }
        }, 2000 * ((attempt || 0) + 1));
      });
    });
  }
  // Re-send any unconfirmed save (on load / when back online) so nothing is left behind.
  function flushPending() {
    if (!backendOn()) return;
    var m = window.HubMember; if (!m || !m.is || !m.is() || !m.id) return;
    var raw = null; try { raw = localStorage.getItem(PENDING_KEY); } catch (e) {}
    if (!raw) return;
    var body = null; try { body = JSON.parse(raw); } catch (e) { return; }
    if (body && String(body.member_id) === String(m.id)) postProfile(body, 0);
  }
  try { window.addEventListener('online', flushPending); } catch (e) {}
  // Fetch ANY member's public profile (for /pages/athlete?id=). cb(profile|null).
  function fetchPublic(id, cb) {
    if (!backendOn() || !id) { cb(null); return; }
    var url = EP + (EP.indexOf('?') < 0 ? '?' : '&') + 'profile=' + encodeURIComponent(id);
    // Send the viewer's OWN id + locker token so the server can return the owner their full
    // profile while stripping privacy-hidden fields (home base, races, PBs, socials) for
    // everyone else — the render-time hide alone left those fields readable in the raw blob.
    try {
      var vm = window.HubMember, vtok = lockerToken();
      if (vm && vm.is && vm.is() && vm.id && vtok) url += '&viewer=' + encodeURIComponent(vm.id) + '&vtoken=' + encodeURIComponent(vtok);
    } catch (e) { /* no-op */ }
    jget(url, function (d) {
      if (!d) { cb(null); return; }
      var prof = d.profile;
      if (typeof prof === 'string') { try { prof = JSON.parse(prof); } catch (e) { prof = null; } }
      if (!prof) prof = d; // backend may already return the fields flat
      prof.id = id;
      if (d.interests && !prof._joins) prof._joins = d.interests.length;
      cb(prof);
    });
  }

  var HYDRATED = false; // true once hydrateOwn has attempted a server pull (gates the avatar auto-seed)
  // Does the profile hold REAL user-entered content? Deliberately IGNORES `name` and
  // `avatarKit`, which the locker auto-seeds from the account/member-id on load — those
  // must NOT count as "the user has data here", or cross-device hydrate never fires.
  function hasRealEdits(p) {
    p = p || get();
    return !!((p.bio || '').trim() || (p.pbsX && p.pbsX.length) || (p.races && p.races.length) ||
      (p.interests && p.interests.length) || p.slug || p.homeBase || p.gender || p.bgHex ||
      p.titleAdj || p.titleNoun || (p.links && (p.links.ig || p.links.strava)));
  }
  // Write fields to the LOCAL profile only — no server sync, and DON'T bump `updated`
  // (so it stays non-authoritative). Used for the deterministic starter avatar so a
  // cosmetic default can never sync a partial profile over the real server copy.
  function patchLocal(obj) {
    var p = get();
    for (var k in obj) if (obj.hasOwnProperty(k)) p[k] = obj[k];
    try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {}
    emit(p);
    return p;
  }
  // Hydrate the OWNER's own locker from the backend. The locker page otherwise only
  // reads localStorage, so a fresh device / cleared cache / incognito shows an empty
  // locker even though the profile is safe on the server. SERVER-AUTHORITATIVE by the
  // `updated` stamp: adopt the server copy unless LOCAL has a strictly newer save (an
  // unsynced local edit). Reads local ONCE up front — safe because nothing writes local
  // before we finish (the avatar seed waits on HYDRATED, and is now local-only anyway).
  // PUT THE ARCHETYPE BACK ON THIS DEVICE. The quiz is the only writer of `hub:archetype`, and
  // every surface that shows an archetype to its owner (the locker, the Do-This-Next rail, the
  // season leaderboard) reads that key and nothing else. sync() has always pushed it to the
  // server; NOTHING ever pulled it down. So a member who signed in on a second device, a second
  // browser, or after clearing their cache saw a locker with no archetype at all — and the only
  // offered way back was "Take the quiz", which computes a NEW title from NEW answers and syncs
  // THAT over the real one.
  //
  // That is the whole "my archetype changed after I logged in with Google" report: not a reset,
  // a forced retake. The two titles in that report are reachable only from different SPORT
  // answers, which is what proved a second run had happened rather than a value being corrupted.
  //
  // Reads `arch` (the full object, synced since 2026-08-12) and falls back to the three flat
  // fields for profiles written before that — degraded (no sport/standing) but a correct title
  // beats a blank locker. A LOCAL archetype that is newer than the server's always wins, so a
  // run finished on this device seconds ago is never overwritten by a slower hydrate.
  function restoreArchetype(srv) {
    if (!srv || typeof srv !== 'object') return false;
    var a = (srv.arch && srv.arch.title) ? srv.arch : null;
    if (!a && srv.archetype) a = { title: srv.archetype, tier: srv.archetypeTier || '', sub: srv.archetypeSub || '' };
    if (!a || !a.title) return false;
    var cur = archetype();
    if (cur && (+cur.ts || 0) >= (+a.ts || 0)) return false;   // a newer local run wins
    // Deliberately NOT short-circuited on "same title, same tier". A local copy can carry the
    // right title and still be POORER than the server's — an older schema with no `sport`, no
    // `standing` — and skipping the write would strand the locker's gear CTA and the challenge
    // comparison forever. The ts guard above already makes this a no-op once they agree, so it
    // cannot loop: after the write, cur.ts === a.ts.
    try { localStorage.setItem('hub:archetype', JSON.stringify(a)); } catch (e) { return false; }
    // The locker paints the archetype once at DOMContentLoaded, long before this fetch returns.
    // Without this event the restore would only become visible on the NEXT page load — i.e. the
    // member would still be looking at the empty locker that talks them into retaking the quiz.
    try { window.dispatchEvent(new CustomEvent('hub:archetype', { detail: a })); } catch (e) {}
    return true;
  }

  function hydrateOwn(cb) {
    cb = cb || function () {};
    var m = window.HubMember;
    if (!backendOn() || !m || !m.is() || !m.id) { HYDRATED = true; cb(false); return; }
    /* WAIT FOR THE SESSION FIRST (2026-10-01), as postProfile already does. fetchPublic sends the
       viewer token that exists at that instant, and at boot that is usually none: the mint is an App
       Proxy round trip still in the air. The server then answers with the PUBLIC copy — which since
       Strava consent has no strava block for a member who has not opted in, and only {connected:true}
       for one who has — and adopting that as the owner's profile hid Unlink and the consent switch.
       ensure() never rejects (a failed mint resolves to the DOM token), and resolves at once when
       the held token is fresh or there is nothing to mint. */
    var sess = (window.HubSession && window.HubSession.ensure) ? window.HubSession : null;
    var gate = (!sess || sess.isFresh()) ? Promise.resolve() : sess.ensure().then(null, function () {});
    gate.then(function () { pull(); });
    function pull() {
    var local = get();                                     // snapshot BEFORE we flip HYDRATED / emit
    fetchPublic(m.id, function (srv) {
      var did = false;
      // Runs on every successful fetch, NOT only when the profile blob is adopted below. A member
      // can hold real local edits newer than the server (keepLocal) and still have no archetype on
      // this device; those are independent facts and restoreArchetype() carries its own guard.
      restoreArchetype(srv);
      if (srv && typeof srv === 'object') {
        var srvT = +srv.updated || 0, locT = +local.updated || 0;
        var serverHasSomething = hasRealEdits(srv) || srv.avatarKit || srv.name;
        // keep local ONLY if it holds a real edit strictly newer than the server
        var keepLocal = hasRealEdits(local) && locT > srvT;
        if (serverHasSomething && !keepLocal) {
          var merged = blank();
          for (var k in srv) if (srv.hasOwnProperty(k) && srv[k] != null) merged[k] = srv[k];
          delete merged.id; delete merged._joins; delete merged.sizedUpBy; delete merged.reports; delete merged.hidden;
          // PRESERVE LOCAL-ONLY FIELDS (privacy fix 2026-07-29). `dob` and `photo` deliberately never
          // cross the wire — dob because the birth YEAR must stay on-device (U18 stealth), photo
          // because a data URL is far too large. Rebuilding from blank() + server keys therefore
          // ERASED both. For dob that was a real privacy hole: u18() then computed false, sync()
          // pushed minor:false, and applyPrivacy() stopped hiding an under-18 member's socials/age.
          var localOnly = get();                            // fresh read, not the pre-fetch snapshot
          ['dob', 'photo'].forEach(function (k) { if (localOnly[k]) merged[k] = localOnly[k]; });
          // NEVER let a server copy empty a collection we still hold locally (2026-07-29).
          // applyPrivacy returns pbs:{}, pbsX:[], races:[] — which are non-null, so the loop
          // above happily copied those blanks over real data. A hydrate must not be able to
          // destroy content; only an explicit edit may. Depth-first defence: the server-side
          // owner-detection fix in the community function is the real one.
          ['pbs', 'pbsX', 'races', 'interests', 'links'].forEach(function (k) {
            var lv = localOnly[k], mv = merged[k];
            var lFull = lv && (Array.isArray(lv) ? lv.length : Object.keys(lv).length);
            var mEmpty = !mv || (Array.isArray(mv) ? !mv.length : !Object.keys(mv).length);
            if (lFull && mEmpty) merged[k] = lv;
          });
          // ...and never let a PUBLIC-shaped strava block (absent, or {connected:true} alone — what
          // the server gives a viewer it could not identify, e.g. after a failed mint) replace the
          // owner's fuller one. The owner's read always carries the whole stored block.
          var ls = localOnly.strava, ms = merged.strava;
          var msPublic = !ms || (typeof ms === 'object' && Object.keys(ms).length === 1 && ms.connected === true);
          if (ls && typeof ls === 'object' && Object.keys(ls).length > 1 && msPublic) merged.strava = ls;
          merged.updated = srvT;
          try { localStorage.setItem(KEY, JSON.stringify(merged)); } catch (e) {}
          did = true;
        }
      }
      HYDRATED = true;                                     // now the avatar seed may run (local-only)
      emit(get());                                         // single repaint → dashboard + avatar render
      cb(did);
    });
    }
  }

  // ---- HEX colour: validate (XSS-safe) + WCAG contrast ---------------------
  // Only a strict #RGB / #RRGGBB string is ever accepted, so nothing but a colour
  // literal can reach a style/Metafield — no CSS/JS injection surface.
  var HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  function validHex(v) { return typeof v === 'string' && HEX_RE.test(v.trim()); }
  function normHex(v) {
    if (!validHex(v)) return '';
    v = v.trim().toLowerCase();
    if (v.length === 4) v = '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
    return v;
  }
  function rgb(hex) { hex = normHex(hex); if (!hex) return null; return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)]; }
  function relLum(hex) { // WCAG relative luminance
    var c = rgb(hex); if (!c) return 0;
    var a = c.map(function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }
  function isLight(hex) { return relLum(hex) > 0.4; } // true → dark ink reads better on it
  function inkFor(hex) { return isLight(hex) ? '#0e1116' : '#f4f6f8'; } // auto-flip profile text

  // ---- age / U18 stealth --------------------------------------------------
  function ageFrom(dob) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || '')) return null;
    var d = new Date(dob + 'T00:00:00'); if (isNaN(d)) return null;
    var now = new Date(), a = now.getFullYear() - d.getFullYear();
    var m = now.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
    return a >= 0 && a < 130 ? a : null;
  }
  function u18(p) { p = p || get(); var a = ageFrom(p.dob); return a != null && a < 18; }

  // ---- profile completion (→ unlocks starter gear) ------------------------
  function completion(p) {
    p = p || get();
    var items = [
      { k: 'avatar', label: 'Build your avatar', done: !!p.avatarKit },
      { k: 'bio', label: 'Write a bio', done: (p.bio || '').trim().length >= 20 },
      { k: 'home', label: 'Set your home base', done: !!(p.homeBase || '').trim() },
      { k: 'gender', label: 'How you show up', done: !!(p.gender || '').trim() },
      // Birthday earns 50 pts on the day itself (loyalty_birthday_run), so it belongs
      // in the checklist that actually drives fills — it was the one field with a real reward attached
      // and no prompt anywhere. Only MM-DD ever leaves the device (see sync()).
      // Accept EITHER the local full dob OR the synced 'MM-DD' bday. Only bday crosses the wire
      // (sync() strips the year), so testing dob alone left this chip permanently unticked on any
      // second device — and unreachable 100% completion with it.
      { k: 'bday', label: 'Add your birthday',
        done: /^\d{4}-\d{2}-\d{2}$/.test(p.dob || '') || /^\d{2}-\d{2}$/.test(p.bday || '') },
      { k: 'interests', label: 'Pick 3+ interests', done: (p.interests || []).length >= 3 },
      { k: 'pb', label: 'Log a personal best', done: pbCount(p) >= 1 },
      { k: 'race', label: 'Log a race result', done: (p.races || []).length >= 1 },
      { k: 'link', label: 'Link a social', done: !!((p.links || {}).ig || (p.links || {}).strava) }
    ];
    var done = 0; items.forEach(function (i) { if (i.done) done++; });
    return { items: items, done: done, total: items.length, pct: Math.round((done / items.length) * 100) };
  }
  // Starter-gear ids unlocked at 100% completion (fed into the avatar's earned list).
  var STARTER_GEAR = ['profile100'];

  // ---- Master Event Dictionary (Build-a-Stat, no free-typing) --------------
  // Prefers the hub_event metaobjects (window.HUB_EVENTS_MO, admin-editable);
  // falls back to a built-in set so the picker always works.
  var EVENT_FALLBACK = [
    { sport:'Running', event:'5K', unit:'time', placeholder:'21:30' },
    { sport:'Running', event:'10K', unit:'time', placeholder:'45:00' },
    { sport:'Running', event:'Half Marathon', unit:'time', placeholder:'1:45:00' },
    { sport:'Running', event:'Marathon', unit:'time', placeholder:'3:45:00' },
    { sport:'Cycling', event:'40K TT', unit:'time', placeholder:'1:05:00' },
    { sport:'Cycling', event:'FTP', unit:'power', placeholder:'250 W' },
    { sport:'Swimming', event:'100m Freestyle', unit:'time', placeholder:'1:05' },
    { sport:'Strength', event:'Back Squat', unit:'weight', placeholder:'140 kg' },
    { sport:'Strength', event:'Deadlift', unit:'weight', placeholder:'180 kg' },
    { sport:'Hyrox', event:'Full (Singles)', unit:'time', placeholder:'1:15:00' }
  ];
  function eventDict() {
    var mo = window.HUB_EVENTS_MO;
    if (mo && mo.length) return mo.map(function (m) { return { sport: m.sport, category: m.category || '', event: m.event, unit: m.unit || 'time', placeholder: m.placeholder || '', sort: +m.sort || 0 }; })
      .sort(function (a, b) { return a.sort - b.sort; });
    return EVENT_FALLBACK.slice();
  }
  function eventSports() { var d = eventDict(), seen = {}, out = []; d.forEach(function (e) { if (e.sport && !seen[e.sport]) { seen[e.sport] = 1; out.push(e.sport); } }); return out; }
  function eventsForSport(sport) { return eventDict().filter(function (e) { return e.sport === sport; }); }
  function eventMeta(sport, ev) { var m = null; eventDict().forEach(function (e) { if (e.sport === sport && e.event === ev) m = e; }); return m; }

  // ---- customer.metafields.stats.pbs JSON schema (clean, export-ready) -----
  // One namespaced blob a future App Proxy can write to the Customer Metafield.
  function pbMetafield(p) {
    p = p || get();
    return {
      schema: 'hub.pbs.v1',
      flat: p.pbs || {},                                     // { '5K':'21:30' } — quick lookups
      events: (p.pbsX || []).map(function (r) {               // structured, dictionary-enforced
        return { sport: r.sport, event: r.event, unit: r.unit || 'time', value: String(r.value || ''), updated: r.updated || '' };
      }),
      updated: new Date().toISOString()
    };
  }

  // ---- timezone-safe day math (Strava stores UTC → avoid the "midnight bug") -
  function localDayKey(when) {
    var d = when ? new Date(when) : new Date();
    if (isNaN(d)) return '';
    var off = d.getTimezoneOffset() * 60000;                 // shift UTC → the viewer's local wall-clock day
    return new Date(d.getTime() - off).toISOString().slice(0, 10);
  }
  function streakDays(datesUTC) {
    // count consecutive LOCAL days ending today; a 23:30-UTC activity counts on the local day it happened.
    var set = {}; (datesUTC || []).forEach(function (t) { var k = localDayKey(t); if (k) set[k] = 1; });
    var streak = 0, cur = new Date();
    for (;;) {
      var key = localDayKey(cur.getTime());
      if (set[key]) { streak++; cur.setDate(cur.getDate() - 1); } else break;
    }
    return streak;
  }

  // ---- Dynamic Title Combining (Adjective + Noun) -------------------------
  // Each word unlocks separately via an achievement id (or 'level:Pro' / '' = always).
  // Merchant can override/extend via hub_title metaobjects (window.HUB_TITLES_MO).
  var TITLES = {
    // NOTE: every `by:` must reference a KEPT achievement id or a level — the ❌ badges
    // were removed, so unlocks were remapped to kept badges (else the word never unlocks).
    adj: [
      { w: 'Certified', by: '' }, { w: 'Feral', by: 'sportkit' }, { w: 'Sweaty', by: 'save1' },
      { w: 'Relentless', by: 'race' }, { w: 'Caffeinated', by: 'story' }, { w: 'Unhinged', by: 'rounded' },
      { w: 'Elite', by: 'level:Elite' }, { w: 'Mythic', by: 'know' }, { w: 'Undefeated', by: 'fin1' },
      { w: 'Seasoned', by: 'founder' }, { w: 'Chaotic', by: 'join1' }, { w: 'Nocturnal', by: 'join3' }, { w: 'Lightning', by: 'frontrow' }
    ],
    noun: [
      { w: 'Rookie', by: '' }, { w: 'Roadrunner', by: 'pb1' }, { w: 'KOM Stealer', by: 'plugged' },
      { w: 'Marathoner', by: 'fin1' }, { w: 'Tri-Beast', by: 'tri' }, { w: 'Hoarder', by: 'save1' },
      { w: 'Ringleader', by: 'join10' }, { w: 'Record Keeper', by: 'builder' }, { w: 'Completionist', by: 'kitted' },
      { w: 'Legend', by: 'level:Legend' }, { w: 'G.O.A.T.', by: 'level:G.O.A.T.' }, { w: 'Vault', by: 'profile100' }, { w: 'Front Row', by: 'frontrow' }, { w: 'Pole-Sitter', by: 'pole26' }
    ]
  };
  function titleVocab() {
    var mo = window.HUB_TITLES_MO;
    if (!mo || !mo.length) return { adj: TITLES.adj.slice(), noun: TITLES.noun.slice() };
    var adj = TITLES.adj.slice(), noun = TITLES.noun.slice();
    mo.forEach(function (m) {
      var w = m.word || m.w; if (!w) return;
      var kind = (m.kind || '').toLowerCase().indexOf('noun') >= 0 ? noun : adj;
      if (!kind.some(function (x) { return x.w === w; })) kind.push({ w: w, by: m.unlock || m.by || '' });
    });
    return { adj: adj, noun: noun };
  }
  // Given an evaluate() state, which words are unlocked.
  function titlesUnlocked(state) {
    var earned = {}; (state.earned || []).forEach(function (a) { earned[a.id] = true; });
    var levelName = state.level, levelIdx = LEVELS.map(function (l) { return l.name; }).indexOf(levelName);
    function ok(by) {
      if (!by) return true;
      if (by.indexOf('level:') === 0) { var want = LEVELS.map(function (l) { return l.name; }).indexOf(by.slice(6)); return want >= 0 && levelIdx >= want; }
      return !!earned[by];
    }
    var v = titleVocab();
    return {
      adj: v.adj.map(function (x) { return { w: x.w, unlocked: ok(x.by), by: x.by }; }),
      noun: v.noun.map(function (x) { return { w: x.w, unlocked: ok(x.by), by: x.by }; })
    };
  }
  function composedTitle(p) {
    if (window.HUB_ARCHITECT) return 'The Architect'; // 1-of-1 easter egg
    p = p || get();
    var a = (p.titleAdj || '').trim(), n = (p.titleNoun || '').trim();
    return (a + ' ' + n).trim();
  }

  // ---- Ego Tracker ("Sized up by") ----------------------------------------
  // Increment a target's counter when they're pulled into a matchup. Backend
  // (App Proxy / Metafield) owns the real tally; inert without an endpoint.
  function sizeUp(targetId) {
    if (!backendOn() || !targetId) return;
    var m = window.HubMember, body = { action: 'sizeup', target_id: targetId, by: (m && m.id) || '', token: lockerToken() };
    if (MODE === 'rest') fetch(EP, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(function () {});
    else { var fd = new URLSearchParams(); for (var k in body) fd.append(k, body[k]); fetch(EP, { method: 'POST', mode: 'no-cors', body: fd }).catch(function () {}); }
  }

  // ---- clean username slugs (+ collision handling) ------------------------
  function slugify(s) {
    return String(s || '').toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || 'athlete';
  }
  // Ask the backend to claim a slug; it returns the final one (aldrich-1 if taken).
  // Without a backend, return the base slug (App Proxy resolves collisions server-side).
  function claimSlug(desired, cb) {
    cb = cb || function () {};
    var base = slugify(desired || (get().name));
    if (!backendOn()) { cb(base); return; }
    var m = window.HubMember;
    // Send the locker token: claiming a handle is a WRITE, and the backend needs a verified identity.
    // Without it the claim is rejected and collision-resolution silently never runs.
    jget(EP + (EP.indexOf('?') < 0 ? '?' : '&') + 'claimslug=' + encodeURIComponent(base)
         + '&member=' + encodeURIComponent((m && m.id) || '')
         + '&token=' + encodeURIComponent(lockerToken()), function (d) {
      cb((d && d.slug) || base);
    });
  }

  // ---- Trust score + Report (anti "troll-swarm") --------------------------
  // A reporter's weight; low-trust accounts can't mass-report a rival off the site.
  function trustScore() {
    var m = window.HubMember, p = get(), s = 0;
    if (m && m.is && m.is()) s += 2;                     // signed-in member
    s += Math.min(3, yearsOn(p));                        // tenure
    s += Math.min(3, Object.keys(p.unlocks || {}).length / 5); // earned history
    return Math.round(s);                                 // ~0..8
  }
  function reportsLog() { try { return JSON.parse(localStorage.getItem('hub:reports') || '{}') || {}; } catch (e) { return {}; } }
  function report(targetId, cb) {
    cb = cb || function () {};
    if (!targetId) { cb({ ok: false, reason: 'no-target' }); return; }
    var log = reportsLog(), now = Date.now(), dayAgo = now - 864e5;
    if (log[targetId] && log[targetId] > dayAgo) { cb({ ok: false, reason: 'already' }); return; }      // 1/profile/24h
    var recent = Object.keys(log).filter(function (k) { return log[k] > dayAgo; }).length;
    if (recent >= 3) { cb({ ok: false, reason: 'rate' }); return; }                                     // ≤3/device/24h (anti-swarm)
    if (trustScore() < 2) { cb({ ok: false, reason: 'trust' }); return; }                               // trust gate
    log[targetId] = now; try { localStorage.setItem('hub:reports', JSON.stringify(log)); } catch (e) {}
    track('profile_report', { target: targetId, trust: trustScore() });
    if (backendOn()) { // backend tallies distinct reports and hides UGC after 3. Inert w/o endpoint.
      var m = window.HubMember, body = { action: 'report', target_id: targetId, reporter_id: (m && m.id) || '', trust: trustScore(), token: lockerToken() };
      if (MODE === 'rest') fetch(EP, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).catch(function () {});
      else { var fd = new URLSearchParams(); for (var k in body) fd.append(k, body[k]); fetch(EP, { method: 'POST', mode: 'no-cors', body: fd }).catch(function () {}); }
    }
    cb({ ok: true });
  }

  // ---- timestamped unlocks (Grandfather Protocol + "Year in Review") -------
  // Stamp each achievement the FIRST time it's earned; never removed even if the
  // rules later change — the stamp is the durable record (early adopters keep it).
  function logUnlocks(earned) {
    var p = get(), changed = false, now = new Date().toISOString(), fresh = [];
    (earned || []).forEach(function (a) { if (a && a.id && !p.unlocks[a.id]) { p.unlocks[a.id] = now; changed = true; fresh.push(a.id); } });
    if (changed) { try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) {} }
    // UNIFIED POINTS: credit each newly-unlocked badge to the redeemable ledger. The server owns the
    // value, is idempotent per member+badge, and rejects the two server-verified badges (firstbuy/refer)
    // — all no-ops. Inert-safe: does nothing until the loyalty backend + award route are live.
    if (fresh.length && window.HubLoyalty && window.HubLoyalty.award) {
      // Award SEQUENTIALLY (not parallel forEach) so the responses can't race the balance paint —
      // each award returns the full state AFTER its own credit, so the last paint reflects the
      // settled balance. A final reload() reconciles in case any award no-op'd. (was: last-response-wins)
      fresh.reduce(function (pr, id) {
        return pr.then(function () { return window.HubLoyalty.award(id); }).catch(function () {});
      }, Promise.resolve()).then(function () {
        if (window.HubLoyalty.reload) window.HubLoyalty.reload();
      });
    }
    return p.unlocks;
  }

  // ---- member search / directory (backend-driven) -------------------------
  function searchMembers(query, cb) {
    if (!backendOn() || !query) { cb(null); return; }
    jget(EP + (EP.indexOf('?') < 0 ? '?' : '&') + 'search=' + encodeURIComponent(query), function (d) {
      cb(d && d.results ? d.results : []);
    });
  }
  function recentMembers(cb) {
    if (!backendOn()) { cb(null); return; }
    jget(EP + (EP.indexOf('?') < 0 ? '?' : '&') + 'members=recent', function (d) {
      cb(d && d.results ? d.results : []);
    });
  }

  // ---- public API ---------------------------------------------------------
  window.HubProfile = {
    KEY: KEY,
    get: get, set: set, patch: patch, patchLocal: patchLocal, persist: persist, on: on,
    interestsList: function () { return INTERESTS.slice(); },
    pbDistances: function () { return PB_DISTANCES.slice(); },
    evaluate: evaluate,
    levels: function () { return LEVELS.slice(); },
    yearsOn: yearsOn,
    archetype: archetype,
    backendOn: backendOn,
    fetchPublic: fetchPublic,
    hydrateOwn: hydrateOwn,
    hydrated: function () { return HYDRATED; },
    searchMembers: searchMembers,
    recentMembers: recentMembers,
    profileUrl: function (id) { return '/pages/athlete?id=' + encodeURIComponent(id || (window.HubMember && window.HubMember.id) || ''); },
    absProfileUrl: function (id) { try { return location.origin + this.profileUrl(id); } catch (e) { return this.profileUrl(id); } },
    // colour helpers
    validHex: validHex, normHex: normHex, isLight: isLight, inkFor: inkFor, relLum: relLum,
    // age / privacy
    ageFrom: ageFrom, u18: u18,
    // completion + titles
    completion: completion, titleVocab: titleVocab, composedTitle: composedTitle,
    // podium / medals + race results
    medals: medals, medalFor: medalFor, topRaces: topRaces,
    // personal bests are now starred Build-a-Stat entries
    featuredPbs: featuredPbs, flatPbs: flatPbs,
    // Build-a-Stat PB engine
    eventDict: eventDict, eventSports: eventSports, eventsForSport: eventsForSport, eventMeta: eventMeta,
    pbMetafield: pbMetafield,
    // timezone-safe streaks (Strava UTC)
    localDayKey: localDayKey, streakDays: streakDays,
    // safety
    trustScore: trustScore, report: report,
    // social: ego tracker + slugs
    sizeUp: sizeUp, slugify: slugify, claimSlug: claimSlug,
    prettyUrl: function (slug) { return slug ? ('/a/profile/' + slugify(slug)) : ''; }, // resolved by the App Proxy (LIVE 2026-09-11)
    // Absolute shareable URL. Prefers the pretty /a/profile/<slug> (the App Proxy 302s it to the
    // id-keyed page) when a slug exists; falls back to the ?id= URL. For COPY / SHARE / QR surfaces
    // only — internal navigation still links by id via profileUrl().
    shareUrl: function (id, slug) {
      var pretty = this.prettyUrl(slug);
      try { return pretty ? (location.origin + pretty) : this.absProfileUrl(id); }
      catch (e) { return pretty || this.absProfileUrl(id); }
    },
    // clean flat record for a future AI vector DB (matchmaking)
    exportVector: function (pOverride) {
      var p = pOverride || get(), a = archetype() || {};
      return {
        schema: 'hub.profile.v1', id: (window.HubMember && window.HubMember.id) || null,
        gender: p.gender || '', homeBase: p.homeBase || '', yearsOn: yearsOn(p),
        interests: (p.interests || []).slice(), sports: eventSportsOf(p),
        archetype: a.title || '', tier: a.tier || '', subTier: a.sub || '',
        standing: a.standing != null ? +a.standing : null,
        titles: [p.titleAdj, p.titleNoun].filter(Boolean), pbCount: pbCount(p),
        pbs: pbMetafield(p), minor: u18(p), updated: new Date().toISOString()
      };
    },
    // analytics passthrough (micro-action data layer)
    track: track
  };
  function eventSportsOf(p) { var s = {}; (p.pbsX || []).forEach(function (r) { if (r.sport) s[r.sport] = 1; }); (p.interests || []).forEach(function (i) { s[i] = 1; }); return Object.keys(s); }

  // keep titles/level fresh when saves or quiz change
  window.addEventListener('hub:saves', function () { emit(get()); });
  window.addEventListener('hub:quiz', function () { emit(get()); });

  // ---- STRANDED-ARCHETYPE RESCUE (2026-08-19) -----------------------------
  // THE HOLE THIS CLOSES. The quiz writes `hub:archetype` and calls persist(); persist's sync()
  // returns early while signed out. The designed funnel is quiz FIRST, sign in after — so at the
  // only moment the archetype is written, there is no member to write it for. The member then
  // signs in, `loyalty` mints their row for the signup points, and `profile` stays NULL forever.
  // That is 238 of 262 members: points, an archetype on their device, no profile, and a card in
  // the directory reading "ATHLETE". Six of them even hold the "found your archetype" achievement
  // — proof the browser had the result AND was talking to the server in the same session, and
  // still never wrote the profile.
  //
  // WHY IT CANNOT LIVE IN hydrateOwn(). That function only ever PULLS, and bootHydrate only ran
  // it on the locker page — the one page these members never reach. A rescue gated behind
  // visiting the locker would fire for exactly the people who did not need it.
  //
  // WHAT IT COSTS: nothing on the overwhelming majority of loads. It needs a signed-in member AND
  // something worth rescuing on this device AND a device that has not already settled the
  // question — so it is one GET, and at most one POST, once per member per device, ever.
  var RESCUED_KEY = 'hub:profile:rescued';
  function rescueStrandedProfile() {
    if (!backendOn()) return;
    var m = window.HubMember;
    if (!m || !m.is || !m.is() || !m.id) return;
    var done = null; try { done = localStorage.getItem(RESCUED_KEY); } catch (e) {}
    if (done === String(m.id)) return;                    // already settled here for this member
    var local = get();
    // `name` deliberately does NOT count as something worth rescuing: get() falls back to the
    // Shopify first name, so testing it would make every signed-in member look like they had
    // edits and would turn this into a GET on every first page view.
    if (!archetype() && !local.avatarKit && !hasRealEdits(local)) return;
    fetchPublic(m.id, function (srv) {
      // A null answer is the network, not a verdict. Leave the flag unset so the next load retries
      // rather than writing off the rescue on one bad request.
      if (!srv) return;
      var srvHas = hasRealEdits(srv) || srv.avatarKit || srv.name || srv.archetype;
      // Narrow on purpose. Only when the server holds NOTHING of its own can a device push its
      // whole profile up — otherwise a stale phone could overwrite a laptop's real work. (The
      // server's mergeProfile keeps the richer value regardless; this guard stops the request.)
      if (!srvHas) {
        sync(local);
        track('profile_rescued', { archetype: !!archetype(), edits: hasRealEdits(local) });
      }
      try { localStorage.setItem(RESCUED_KEY, String(m.id)); } catch (e) {}
    });
  }

  // On the locker page, pull the owner's profile down from the backend once on load
  // (fixes "edits don't persist across devices" — the locker otherwise reads only
  // localStorage). Gated to the locker section so it's one backend GET, not one per page.
  function bootHydrate() {
    // hub-profile.js (deferred, in <head>) executes BEFORE hub-membership.js defines
    // window.HubMember, so wait for it — otherwise everything below bails with "no member".
    var tries = 0;
    (function waitForMember() {
      if ((window.HubMember && window.HubMember.is) || tries++ > 50) {   // ~5s fallback
        // BOTH OF THESE ARE NOW SITE-WIDE, and both used to be locker-only. An unconfirmed save
        // was only ever retried on the locker page, which is the page a member with a broken save
        // is least likely to reach; and the rescue above is for members who never reach it at all.
        // Each no-ops safely without a member, and neither touches the network without a reason.
        flushPending();
        rescueStrandedProfile();
        // The full pull stays locker-only: it is a second backend GET, and only the locker renders
        // enough of the profile to need one. On a first locker visit a rescued member pays both,
        // once — after that the RESCUED_KEY flag means it is the pull alone.
        if (document.getElementById('locker') || document.getElementById('lk-preview')) hydrateOwn();
        return;
      }
      setTimeout(waitForMember, 100);
    })();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootHydrate);
  else bootHydrate();
})();
